import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  ListItemText,
  List,
  ListItem,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import productService from "../../../services/productService";
import { toast } from "react-toastify";

/**
 * Helper function to generate all possible combinations of attribute values
 * @param {Array} attributes - Array of attribute objects with values
 * @returns {Array} - Array of objects representing all possible combinations
 */
const generateAttributeCombinations = (attributes) => {
  if (!attributes || attributes.length === 0) return [];

  const attributesWithValues = attributes.map((attr) => {
    const values = attr.attributeValues || [];
    return {
      attributeId: attr.attributeId || attr.id,
      attributeName: attr.name,
      values: values.map((v) => ({
        valueId: v.id || v.valueId,
        value: v.value,
      })),
    };
  });

  // Function to recursively build combinations
  const buildCombinations = (attrIndex, currentCombination = {}) => {
    if (attrIndex >= attributesWithValues.length) {
      return [currentCombination];
    }

    const currentAttr = attributesWithValues[attrIndex];
    const results = [];

    for (const val of currentAttr.values) {
      const newCombination = { ...currentCombination };
      newCombination[currentAttr.attributeName] = val.value;
      newCombination[`${currentAttr.attributeName}_id`] = val.valueId;

      results.push(...buildCombinations(attrIndex + 1, newCombination));
    }

    return results;
  };

  return buildCombinations(0);
};

/**
 * Component for generating product variations based on selected attributes
 *
 * @param {Object} props - Component props
 * @param {string} props.productId - ID of the parent product
 * @param {Array} props.categoryAttributes - List of attributes for the product's category
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onSuccess - Callback function to run after successfully generating variations
 * @returns {JSX.Element} Variation Generator component
 */
const VariationGenerator = ({
  productId,
  categoryAttributes,
  loading,
  onSuccess,
}) => {
  const { t } = useTranslation();

  // Get the parent component's selected attributes for variations from localStorage if they exist
  const getInitialAttributes = () => {
    try {
      const savedData = localStorage.getItem(
        `variation_attributes_${productId}`
      );
      return savedData ? JSON.parse(savedData) : [];
    } catch (error) {
      console.error("Error loading saved variation attributes:", error);
      return [];
    }
  };

  const [selectedAttributes, setSelectedAttributes] =
    useState(getInitialAttributes);
  const [variatableAttributes, setVariatableAttributes] = useState([]);
  const [previewVariations, setPreviewVariations] = useState([]);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedVariatableAttributes, setSelectedVariatableAttributes] =
    useState([]);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Save selected attributes to localStorage whenever they change
  useEffect(() => {
    if (productId) {
      localStorage.setItem(
        `variation_attributes_${productId}`,
        JSON.stringify(selectedAttributes)
      );
    }
  }, [selectedAttributes, productId]);

  // Reset the form when the productId changes
  useEffect(() => {
    if (productId) {
      // Only load from localStorage when the productId changes
      const savedData = localStorage.getItem(
        `variation_attributes_${productId}`
      );
      if (savedData) {
        try {
          setSelectedAttributes(JSON.parse(savedData));
        } catch (error) {
          console.error("Error parsing saved attributes:", error);
          setSelectedAttributes([]);
        }
      } else {
        setSelectedAttributes([]);
      }

      // Also reset the selected variations when the product changes
      setSelectedVariations({});
      setError(null);
      setSuccess(null);
      setSelectedVariatableAttributes([]);
      setSuccessMessage(null);
      setPreviewVariations([]);
      setShowPreview(false);
    }
  }, [productId]);

  // Step 1: Filter only the variatable attributes from the category attributes
  useEffect(() => {
    if (categoryAttributes && categoryAttributes.length > 0) {
      // Extract only the variatable attributes
      const variatable = categoryAttributes.filter(
        (attr) =>
          (attr?.CategoryAttribute?.isVariantable ||
            attr?.categoryAttribute?.isVariantable ||
            attr.isVariantable) === true
      );
      setVariatableAttributes(variatable);
    } else {
      setVariatableAttributes([]);
    }
  }, [categoryAttributes]);

  // Step 2: Generate attribute combinations when selected attributes change
  useEffect(() => {
    if (!selectedAttributes || selectedAttributes.length === 0) {
      setPreviewVariations([]);
      return;
    }

    try {
      // Get the attribute objects for the selected IDs
      const selectedAttributeObjects = selectedAttributes
        .map((id) => variatableAttributes.find((attr) => attr.id === id))
        .filter((attr) => attr); // Filter out undefined

      if (selectedAttributeObjects.length === 0) {
        setPreviewVariations([]);
        return;
      }

      // Generate all possible combinations of attribute values
      const combinations = generateAttributeCombinations(
        selectedAttributeObjects
      );

      // Create preview variations
      const previewVars = combinations.map((combo, index) => {
        // Create a name and SKU suffix based on the combination
        const nameSuffix = Object.entries(combo)
          .map(([attrName, value]) => `${value}`)
          .join(", ");

        // Create a SKU suffix based on the combination (more condensed)
        const skuSuffix = Object.entries(combo)
          .map(([attrName, value]) =>
            value.toString().replace(/\s+/g, "-").substring(0, 5)
          )
          .join("-");

        return {
          id: `preview-${index}`,
          name: `${nameSuffix}`,
          sku: `${skuSuffix}`,
          attributes: Object.entries(combo).map(([attrName, value]) => {
            // Find the attribute and attribute ID
            const attribute = selectedAttributeObjects.find(
              (a) => a.name === attrName
            );
            return {
              attributeId: attribute?.id,
              attributeName: attrName,
              value,
            };
          }),
        };
      });

      setPreviewVariations(previewVars);
    } catch (error) {
      console.error("Error generating variations:", error);
      setError(t("products.errorGeneratingVariations"));
      setPreviewVariations([]);
    }
  }, [selectedAttributes, variatableAttributes]);

  // Helper to check if an attribute is variatable
  const isVariatableAttribute = (attribute) => {
    return (
      attribute?.CategoryAttribute?.isVariantable ||
      attribute?.categoryAttribute?.isVariantable ||
      attribute.isVariantable ||
      false
    );
  };

  // Filter to only show variatable attributes
  const variatableAttributesFiltered = categoryAttributes.filter(
    isVariatableAttribute
  );

  // Handle attribute selection
  const handleAttributeChange = (e) => {
    const selectedIds = e.target.value;
    // Limit to maximum 3 attributes
    if (selectedIds.length > 3) {
      toast.warning(t("products.maxVariationAttributes"));
      return;
    }

    setSelectedAttributes(selectedIds);
  };

  // Handle variation selection checkbox
  const handleVariationSelect = (index) => {
    setSelectedVariations((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Handle "Select All" checkbox
  const handleSelectAll = (event) => {
    const { checked } = event.target;
    const newSelected = {};

    previewVariations.forEach((_, index) => {
      newSelected[index] = checked;
    });

    setSelectedVariations(newSelected);
  };

  // Check if all variations are selected
  const areAllSelected = () => {
    return (
      previewVariations.length > 0 &&
      previewVariations.every((_, index) => selectedVariations[index])
    );
  };

  // Get count of selected variations
  const getSelectedCount = () => {
    return Object.values(selectedVariations).filter((v) => v).length;
  };

  // Filter only variatable attributes
  const variatableAttributesMemo = useMemo(() => {
    return categoryAttributes.filter((attr) => attr.isVariantable);
  }, [categoryAttributes]);

  // Handle checkbox change for selecting variatable attributes
  const handleAttributeSelection = (attributeId) => {
    setSelectedVariatableAttributes((prev) => {
      const isSelected = prev.includes(attributeId);
      if (isSelected) {
        return prev.filter((id) => id !== attributeId);
      } else {
        // Limit to 3 attributes
        if (prev.length >= 3) {
          setError(t("products.maxVariatableAttributes"));
          return prev;
        }
        return [...prev, attributeId];
      }
    });
    setError(null);
  };

  // Generate preview of variations based on selected attributes
  const handlePreviewVariations = () => {
    if (selectedVariatableAttributes.length === 0) {
      setError(t("products.selectVariatableAttributes"));
      return;
    }

    setError(null);
    setShowPreview(true);

    // Find the selected attributes
    const selectedAttrs = variatableAttributesMemo.filter((attr) =>
      selectedVariatableAttributes.includes(attr.id)
    );

    // Generate all possible combinations
    const combinations = generateAttributeCombinations(selectedAttrs);

    // Create preview variations
    const preview = combinations.map((combination, index) => ({
      id: `preview-${index}`,
      name: formatVariationName(combination),
      attributes: combination,
    }));

    setPreviewVariations(preview);
    setSuccessMessage(
      t("products.previewGenerated", { count: preview.length })
    );
  };

  // Generate actual variations
  const handleGenerateVariations = async () => {
    if (selectedVariatableAttributes.length === 0) {
      setError(t("products.selectVariatableAttributes"));
      return;
    }

    if (!productId) {
      setError(t("products.saveProductFirst"));
      return;
    }

    console.log("Generating variations for product ID:", productId);
    console.log(
      "Selected variatable attributes:",
      selectedVariatableAttributes
    );

    try {
      setGenerateLoading(true);
      setError(null);

      const response = await productService.generateVariations(productId, {
        attributeIds: selectedVariatableAttributes,
      });

      console.log("Generation response:", response);
      setSuccessMessage(
        t("products.variationsGenerated", { count: response.length })
      );

      if (onSuccess && typeof onSuccess === "function") {
        onSuccess(response);
      }
    } catch (error) {
      console.error("Error generating variations:", error);
      setError(error.response?.data?.message || t("products.generationFailed"));
    } finally {
      setGenerateLoading(false);
    }
  };

  // Format the name for a variation based on the attribute combination
  const formatVariationName = (combination) => {
    // Handle when combination is an object with attributes array (used in previewVariations)
    if (
      combination &&
      combination.attributes &&
      Array.isArray(combination.attributes)
    ) {
      return combination.attributes
        .map((attr) => `${attr.name}: ${attr.value}`)
        .join(", ");
    }

    // Handle when combination is already an array of attributes
    if (Array.isArray(combination)) {
      return combination
        .map((attr) => `${attr.name || attr.attributeName}: ${attr.value}`)
        .join(", ");
    }

    // Handle simple object with attribute/value pairs
    if (combination && typeof combination === "object") {
      return Object.entries(combination)
        .filter(([key]) => !key.endsWith("_id")) // Skip the ID fields
        .map(([attr, value]) => `${attr}: ${value}`)
        .join(", ");
    }

    // Fallback
    return combination?.name || "Variation";
  };

  // Check if there are any variatable attributes available
  const hasVariatableAttributes = variatableAttributesMemo.length > 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("products.variationGenerator")}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {t("products.variationDescription")}
      </Typography>

      {/* Show alert if no variatable attributes are available */}
      {variatableAttributesMemo.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("products.noVariatableAttributes")}
        </Alert>
      ) : (
        <>
          {/* Step 1: Select attributes to use for variations */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t("products.selectVariationAttributes")}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t("products.variationAttributeHelp")}
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("products.attributes")}</InputLabel>
              <Select
                multiple
                value={selectedAttributes}
                onChange={handleAttributeChange}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => {
                      const attr = variatableAttributesMemo.find(
                        (a) => a.id === value
                      );
                      return (
                        <Chip
                          key={value}
                          label={attr ? attr.name : value}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {variatableAttributesMemo.map((attribute) => (
                  <MenuItem key={attribute.id} value={attribute.id}>
                    <Checkbox
                      checked={selectedAttributes.indexOf(attribute.id) > -1}
                    />
                    <ListItemText primary={attribute.name} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {t("products.maxVariationAttributesHelp")}
              </FormHelperText>
            </FormControl>

            {/* Preview of value combinations if attributes are selected */}
            {selectedAttributes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("products.selectedAttributeValues")}
                </Typography>
                <Grid container spacing={2}>
                  {selectedAttributes.map((attrId) => {
                    const attribute = variatableAttributesMemo.find(
                      (a) => a.id === attrId
                    );
                    if (!attribute) return null;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={attribute.id}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 1, height: "100%", overflow: "auto" }}
                        >
                          <Typography variant="subtitle2">
                            {attribute.name}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <List dense>
                            {attribute.values && attribute.values.length > 0 ? (
                              attribute.values.map((val) => (
                                <ListItem key={val.id}>
                                  <ListItemText primary={val.value} />
                                </ListItem>
                              ))
                            ) : (
                              <ListItem>
                                <ListItemText
                                  primary={t("products.noValuesForAttribute")}
                                />
                              </ListItem>
                            )}
                          </List>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Step 2: Preview variations */}
          {previewVariations.length > 0 && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {t("products.previewVariations")}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={areAllSelected()}
                        onChange={handleSelectAll}
                        indeterminate={
                          getSelectedCount() > 0 &&
                          getSelectedCount() < previewVariations.length
                        }
                      />
                    }
                    label={t("products.selectAll")}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t("products.variationSummary", {
                      count: getSelectedCount(),
                    })}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={1}>
                  {previewVariations.map((combination, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          bgcolor: selectedVariations[index]
                            ? "action.selected"
                            : "background.paper",
                        }}
                      >
                        <Checkbox
                          checked={selectedVariations[index] || false}
                          onChange={() => handleVariationSelect(index)}
                        />
                        <Box>
                          <Typography variant="body2">
                            {formatVariationName(combination)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePreviewVariations}
                  disabled={getSelectedCount() === 0 || generateLoading}
                  startIcon={
                    generateLoading ? <CircularProgress size={20} /> : null
                  }
                >
                  {generateLoading
                    ? t("common.processing")
                    : t("products.previewVariations")}
                </Button>
              </CardActions>
            </Card>
          )}

          {/* Show error or success message */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {successMessage}
            </Alert>
          )}
        </>
      )}

      {showPreview && previewVariations.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t("products.previewTitle")} ({previewVariations.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <List dense>
            {previewVariations.slice(0, 10).map((variation, index) => (
              <ListItem key={index}>
                <ListItemText primary={formatVariationName(variation)} />
              </ListItem>
            ))}
            {previewVariations.length > 10 && (
              <ListItem>
                <ListItemText
                  primary={t("products.andMore", {
                    count: previewVariations.length - 10,
                  })}
                  secondary={t("products.totalVariations", {
                    count: previewVariations.length,
                  })}
                />
              </ListItem>
            )}
          </List>

          <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateVariations}
              disabled={generateLoading || !productId}
              startIcon={
                generateLoading && (
                  <CircularProgress size={20} color="inherit" />
                )
              }
            >
              {generateLoading
                ? t("common.loading")
                : t("products.generateVariations")}
            </Button>
          </Box>
        </Box>
      )}

      {!showPreview && (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            {variatableAttributesMemo.map((attribute) => (
              <Grid item xs={12} sm={6} md={4} key={attribute.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedVariatableAttributes.includes(
                        attribute.id
                      )}
                      onChange={() => handleAttributeSelection(attribute.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{attribute.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {attribute.values?.length || 0} {t("products.values")}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePreviewVariations}
              disabled={selectedVariatableAttributes.length === 0}
            >
              {t("products.previewVariations")}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VariationGenerator;
